import { useMemo } from 'react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card'
import {
  BarChart,
  Bar,
  XAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts'
import { ChartContainer, ChartTooltipContent } from '@/components/ui/chart'
import { useI18n } from '@/store/I18nContext'

export function FinancialIntelligence() {
  const { t } = useI18n()

  const data = useMemo(
    () => [
      { name: t('fin.mon'), rotativo: 400, mensalista: 200 },
      { name: t('fin.tue'), rotativo: 300, mensalista: 200 },
      { name: t('fin.wed'), rotativo: 550, mensalista: 200 },
      { name: t('fin.thu'), rotativo: 450, mensalista: 200 },
      { name: t('fin.fri'), rotativo: 700, mensalista: 200 },
      { name: t('fin.sat'), rotativo: 800, mensalista: 200 },
      { name: t('fin.sun'), rotativo: 600, mensalista: 200 },
    ],
    [t],
  )

  return (
    <section className="py-20 bg-muted/30">
      <div className="container mx-auto px-4 grid lg:grid-cols-2 gap-12 items-center">
        <div className="space-y-6 animate-fade-in-up order-2 lg:order-1">
          <h2 className="text-3xl md:text-5xl font-extrabold tracking-tighter leading-tight">
            {t('fin.title')}
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed font-medium">
            {t('fin.subtitle')}
          </p>
          <div className="space-y-4 pt-2">
            <div className="flex items-center gap-3 text-foreground font-semibold">
              <div className="w-8 h-8 rounded bg-background shadow-sm border flex items-center justify-center text-emerald-600">
                ✓
              </div>
              {t('fin.f1')}
            </div>
            <div className="flex items-center gap-3 text-foreground font-semibold">
              <div className="w-8 h-8 rounded bg-background shadow-sm border flex items-center justify-center text-emerald-600">
                ✓
              </div>
              {t('fin.f2')}
            </div>
            <div className="flex items-center gap-3 text-foreground font-semibold">
              <div className="w-8 h-8 rounded bg-background shadow-sm border flex items-center justify-center text-emerald-600">
                ✓
              </div>
              {t('fin.f3')}
            </div>
          </div>
        </div>

        <div className="animate-fade-in order-1 lg:order-2">
          <Card className="shadow-xl border">
            <CardHeader>
              <CardTitle className="font-bold tracking-tight">
                {t('fin.chartTitle')}
              </CardTitle>
              <CardDescription className="font-medium">
                {t('fin.chartDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent className="h-[300px]">
              <ChartContainer
                config={{
                  rotativo: {
                    label: t('fin.rotary'),
                    color: 'hsl(var(--primary))',
                  },
                  mensalista: {
                    label: t('fin.monthly'),
                    color: 'hsl(var(--secondary))',
                  },
                }}
                className="h-full w-full"
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={data}
                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="hsl(var(--border))"
                    />
                    <XAxis
                      dataKey="name"
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                      className="font-medium"
                    />
                    <RechartsTooltip
                      content={<ChartTooltipContent />}
                      cursor={{ fill: 'hsl(var(--muted))' }}
                    />
                    <Bar
                      dataKey="mensalista"
                      stackId="a"
                      fill="var(--color-mensalista)"
                      radius={[0, 0, 4, 4]}
                    />
                    <Bar
                      dataKey="rotativo"
                      stackId="a"
                      fill="var(--color-rotativo)"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  )
}
